import { useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { UserContext } from "../context/UserContext";
import * as CONSTANTS from "../utils/constants/constants";

export interface MissionAgentMessage {
  type: string;
  event_type?: string;
  organization_id?: string;
  device_id?: string;
  intervention_id?: string | null;
  timestamp?: string;
  data?: Record<string, unknown>;
  result?: {
    summary?: string;
    skill_results?: Array<Record<string, unknown>>;
    requires_review?: boolean;
  };
  previous_intervention_id?: string | null;
}

interface MissionAgentSocketState {
  status: "connecting" | "connected" | "disconnected" | "error";
  messages: MissionAgentMessage[];
  currentInterventionId: string | null;
}

const useMissionAgentSocket = (deviceId?: string): MissionAgentSocketState => {
  const userCtx = useContext(UserContext);
  const [cookies] = useCookies(["access_token"]);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<MissionAgentSocketState>({
    status: "disconnected",
    messages: [],
    currentInterventionId: null,
  });

  useEffect(() => {
    if (!deviceId || !userCtx?.user?.organization) return undefined;

    let mounted = true;
    const connect = () => {
      if (!mounted) return;
      setState((previous) => ({ ...previous, status: "connecting" }));
      const socketUrl = `${CONSTANTS.WS_HOST()}ws/missionagent/${encodeURIComponent(deviceId)}/`;
      const socket = new WebSocket(
        socketUrl,
        cookies.access_token ? [cookies.access_token] : [],
      );
      socketRef.current = socket;

      socket.onopen = () => {
        if (!mounted) {
          socket.close();
          return;
        }
        if (mounted) setState((previous) => ({ ...previous, status: "connected" }));
      };

      socket.onmessage = (event) => {
        if (!mounted || !event.data) return;
        try {
          const message = JSON.parse(event.data) as MissionAgentMessage;
          if (message.type === "missionagent_reset") {
            setState({
              status: "connected",
              messages: [message],
              currentInterventionId: message.intervention_id ?? null,
            });
            return;
          }
          setState((previous) => ({
            status: "connected",
            messages: [message, ...previous.messages].slice(0, 50),
            currentInterventionId:
              message.intervention_id ?? previous.currentInterventionId,
          }));
        } catch (error) {
          console.error("Unable to parse Mission Agent WebSocket message", error);
        }
      };

      socket.onerror = () => {
        if (mounted) setState((previous) => ({ ...previous, status: "error" }));
      };

      socket.onclose = () => {
        if (!mounted) return;
        setState((previous) => ({ ...previous, status: "disconnected" }));
        reconnectRef.current = setTimeout(connect, 3000);
      };
    };

    connect();
    return () => {
      mounted = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      const socket = socketRef.current;
      if (socket && socket.readyState !== WebSocket.CONNECTING) {
        socket.close();
      }
      socketRef.current = null;
    };
  }, [cookies.access_token, deviceId, userCtx?.user?.organization]);

  return state;
};

export default useMissionAgentSocket;
